/**
 * CUEシートパーサー
 *
 * CUEシートのテキストをパースしてチャプター情報の配列を返す。
 * TRACK / TITLE / INDEX 01 の情報を抽出する。
 */

export type Chapter = {
  index: number
  title: string
  /** 開始時刻（秒） */
  startTime: number
}

/**
 * CUE の INDEX タイムコード (MM:SS:FF) を秒に変換する。
 * FF はフレーム (1/75秒) 単位。
 */
const parseTimecode = (timecode: string): number => {
  const parts = timecode.split(':')
  if (parts.length !== 3) return 0

  const minutes = parseInt(parts[0], 10) || 0
  const seconds = parseInt(parts[1], 10) || 0
  const frames = parseInt(parts[2], 10) || 0

  return minutes * 60 + seconds + frames / 75
}

/**
 * CUEシートのテキストをパースしてチャプター配列を返す
 */
export const parseCueSheet = (cueText: string): Chapter[] => {
  const chapters: Chapter[] = []
  const lines = cueText.split('\n')

  let currentTrack = -1
  let currentTitle = ''

  for (const rawLine of lines) {
    const line = rawLine.trim()

    // TRACK 番号を抽出
    const trackMatch = line.match(/^TRACK\s+(\d+)\s+AUDIO/)
    if (trackMatch) {
      currentTrack = parseInt(trackMatch[1], 10)
      currentTitle = `Track ${currentTrack}`
      continue
    }

    // TITLE を抽出（ダブルクオートで囲まれた文字列）
    const titleMatch = line.match(/^\s*TITLE\s+"(.+)"/)
    if (titleMatch && currentTrack >= 0) {
      currentTitle = titleMatch[1]
      continue
    }

    // INDEX 01 のタイムコードを抽出
    const indexMatch = line.match(/^\s*INDEX\s+01\s+(\d+:\d+:\d+)/)
    if (indexMatch && currentTrack >= 0) {
      chapters.push({
        index: currentTrack,
        title: currentTitle,
        startTime: parseTimecode(indexMatch[1]),
      })
      continue
    }
  }

  return chapters
}

/**
 * 現在の再生位置から該当するチャプターのインデックスを返す
 */
export const getCurrentChapterIndex = (chapters: Chapter[], currentTime: number): number => {
  if (chapters.length === 0) return -1

  for (let i = chapters.length - 1; i >= 0; i--) {
    if (currentTime >= chapters[i].startTime) {
      return i
    }
  }

  return 0
}

/**
 * 秒を表示用の時間文字列に変換する (HH:MM:SS or MM:SS)
 */
export const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'

  const totalSeconds = Math.floor(seconds)
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }
  return `${m}:${s.toString().padStart(2, '0')}`
}
